import {
  CatalogProduct,
  CultureSubsidySettings,
  OperationConflict,
  PocketBalances,
  PocketLine,
  PocketKind,
  RuleSource,
  SpecialOperation,
  SubsidyComputation
} from '../types'

export function isOperationActive(operation: SpecialOperation, now = new Date()): boolean {
  const start = new Date(`${operation.startDate}T00:00:00`)
  const end = new Date(`${operation.endDate}T23:59:59`)
  return now >= start && now <= end
}

export function fallbackCapacity(settings: CultureSubsidySettings, pockets: PocketBalances): number {
  let total = 0
  if (settings.fallbackLoyalty) total += pockets.loyalty
  if (settings.fallbackFree) total += pockets.free
  if (settings.fallbackRecycling) total += pockets.recycling
  return total
}

export function pocketOrder(settings: CultureSubsidySettings): PocketKind[] {
  const order: PocketKind[] = ['culture_reserve']
  if (settings.fallbackLoyalty) order.push('loyalty')
  if (settings.fallbackFree) order.push('free')
  if (settings.fallbackRecycling) order.push('recycling')
  return order
}

export function pocketAvailable(pocket: PocketKind, pockets: PocketBalances): number {
  if (pocket === 'culture_reserve') return pockets.cultureReserve
  if (pocket === 'loyalty') return pockets.loyalty
  if (pocket === 'free') return pockets.free
  return pockets.recycling
}

export function findOperationConflicts(operations: SpecialOperation[]): OperationConflict[] {
  const conflicts: OperationConflict[] = []

  for (let i = 0; i < operations.length; i++) {
    for (let j = i + 1; j < operations.length; j++) {
      const a = operations[i]
      const b = operations[j]
      if (!datesOverlap(a, b)) continue

      if (a.type === 'global' && b.type === 'global') {
        conflicts.push({
          kind: 'forbidden',
          messageKey: 'cultureSubsidy.conflicts.twoGlobals',
          operationIds: [a.id, b.id]
        })
      } else if (
        a.type === 'per_product' &&
        b.type === 'per_product' &&
        a.productId &&
        a.productId === b.productId
      ) {
        conflicts.push({
          kind: 'forbidden',
          messageKey: 'cultureSubsidy.conflicts.sameProduct',
          operationIds: [a.id, b.id]
        })
      } else if (a.type !== b.type) {
        conflicts.push({
          kind: 'warning',
          messageKey: 'cultureSubsidy.conflicts.globalAndProduct',
          operationIds: [a.id, b.id]
        })
      }
    }
  }

  return conflicts
}

function datesOverlap(a: SpecialOperation, b: SpecialOperation): boolean {
  return a.startDate <= b.endDate && b.startDate <= a.endDate
}

export interface ResolvedRule {
  source: RuleSource
  rate: number | null
  value: number | null
  operationName: string | null
}

export function resolveApplicableRule(
  product: CatalogProduct,
  settings: CultureSubsidySettings,
  operations: SpecialOperation[],
  now = new Date()
): ResolvedRule {
  const active = operations.filter(operation => isOperationActive(operation, now))

  const productOp = active.find(operation => operation.type === 'per_product' && operation.productId === product.id)
  if (productOp) {
    return {
      source: 'special_product',
      rate: productOp.boostedRate,
      value: productOp.boostedValue,
      operationName: productOp.name
    }
  }

  const globalOp = active.find(operation => operation.type === 'global')
  if (globalOp) {
    return {
      source: 'special_global',
      rate: globalOp.boostedRate,
      value: globalOp.boostedValue,
      operationName: globalOp.name
    }
  }

  if (settings.perProductValueMode && product.valuePerProduct != null) {
    return {
      source: 'product_value',
      rate: null,
      value: product.valuePerProduct,
      operationName: null
    }
  }

  return {
    source: 'standard_rate',
    rate: settings.standardRate,
    value: null,
    operationName: null
  }
}

export function theoreticalAmount(rule: ResolvedRule, orderAmount: number): number {
  if (rule.value != null) return roundMoney(rule.value)
  return roundMoney((rule.rate || 0) * orderAmount)
}

export function computeSubsidy(params: {
  product: CatalogProduct
  settings: CultureSubsidySettings
  operations: SpecialOperation[]
  pockets: PocketBalances
  orderAmount: number
  beneficiaryConsumed: number
  now?: Date
}): SubsidyComputation {
  const now = params.now || new Date()
  const steps: string[] = []

  if (!params.product.eligible) {
    return emptyComputation(params.orderAmount, 'Produit non éligible à la subvention culture.')
  }

  const remainingAnnual = roundMoney(Math.max(0, params.settings.annualBeneficiaryCapping - params.beneficiaryConsumed))
  if (remainingAnnual <= 0) {
    return emptyComputation(params.orderAmount, 'Plafond annuel du bénéficiaire atteint.')
  }

  const rule = resolveApplicableRule(params.product, params.settings, params.operations, now)
  const theoretical = theoreticalAmount(rule, params.orderAmount)
  steps.push(`Paramétrage retenu : ${ruleLabel(rule)}.`)
  steps.push(`Montant théorique : ${formatEuro(theoretical)}.`)

  const afterOrderCap = roundMoney(Math.min(theoretical, params.settings.orderCapping))
  if (afterOrderCap < theoretical) {
    steps.push(`Capping commande : ${formatEuro(afterOrderCap)} (plafond ${formatEuro(params.settings.orderCapping)}).`)
  } else {
    steps.push(`Capping commande non atteint (${formatEuro(params.settings.orderCapping)}).`)
  }

  const afterAnnualCap = roundMoney(Math.min(afterOrderCap, remainingAnnual))
  const prorataAnnualGap = roundMoney(afterOrderCap - afterAnnualCap)
  if (prorataAnnualGap > 0) {
    steps.push(`Prorata plafond annuel : ${formatEuro(afterAnnualCap)} (reste disponible ${formatEuro(remainingAnnual)}).`)
  } else {
    steps.push(`Plafond annuel restant : ${formatEuro(remainingAnnual)}.`)
  }

  const { lines, uncovered } = reserveFromPockets(afterAnnualCap, params.settings, params.pockets)
  const effectiveAmount = roundMoney(afterAnnualCap - uncovered)
  if (uncovered > 0) {
    steps.push(`Fonds insuffisants : subvention ramenée à ${formatEuro(effectiveAmount)}.`)
  } else {
    steps.push(`Réservation couverte selon l’ordre des poches.`)
  }

  return {
    eligible: true,
    ruleSource: rule.source,
    ruleLabel: ruleLabel(rule),
    theoreticalAmount: theoretical,
    afterOrderCap,
    afterAnnualCap,
    effectiveAmount,
    remainingToPay: roundMoney(params.orderAmount - effectiveAmount),
    prorataAnnualGap,
    prorataFundsGap: uncovered,
    remainingAnnual,
    pocketBreakdown: lines,
    steps
  }
}

function reserveFromPockets(
  needed: number,
  settings: CultureSubsidySettings,
  pockets: PocketBalances
): { lines: PocketLine[]; uncovered: number } {
  let remaining = needed
  const lines: PocketLine[] = []

  pocketOrder(settings).forEach(pocket => {
    if (remaining <= 0) return
    const take = roundMoney(Math.min(remaining, pocketAvailable(pocket, pockets)))
    if (take > 0) {
      lines.push({ pocket, amount: take })
      remaining = roundMoney(remaining - take)
    }
  })

  return { lines, uncovered: remaining }
}

function emptyComputation(orderAmount: number, reason: string): SubsidyComputation {
  return {
    eligible: false,
    ruleSource: null,
    ruleLabel: reason,
    theoreticalAmount: 0,
    afterOrderCap: 0,
    afterAnnualCap: 0,
    effectiveAmount: 0,
    remainingToPay: orderAmount,
    prorataAnnualGap: 0,
    prorataFundsGap: 0,
    remainingAnnual: 0,
    pocketBreakdown: [],
    steps: [reason]
  }
}

export function ruleLabel(rule: ResolvedRule): string {
  if (rule.source === 'special_product') {
    return rule.operationName ? `Opération spéciale produit (${rule.operationName})` : 'Opération spéciale produit'
  }
  if (rule.source === 'special_global') {
    return rule.operationName ? `Opération spéciale globale (${rule.operationName})` : 'Opération spéciale globale'
  }
  if (rule.source === 'product_value') return 'Valeur fixe du produit'
  return 'Taux standard'
}

export function roundMoney(value: number): number {
  return Math.round(value * 100) / 100
}

function formatEuro(value: number): string {
  return `${value.toFixed(2).replace('.', ',')} €`
}
